'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  buildEmailNotificationLandingRequest,
  buildVerifiedEmailAttribution,
  EmailNotificationLandingRequest,
  EmailNotificationLandingResponse,
  EMAIL_ATTRIBUTION_CONTEXT_CHANGED_EVENT,
  EmailNotificationSurface,
  parseEmailNotificationAttribution,
  readEmailAttributionDestinationReference,
  readPendingEmailAttributions,
  removePendingEmailAttribution,
  storePendingEmailAttribution,
  storeVerifiedEmailAttribution,
  stripEmailAttributionQuery,
  stripSensitiveEmailAttributionQuery,
  markWebEmailAttributionVerificationCompleted,
  markWebEmailAttributionVerificationStarted,
  EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY,
} from '../../analytics/email-notification-attribution';

type VerifyEmailNotificationLanding = (
  request: EmailNotificationLandingRequest,
) => Promise<EmailNotificationLandingResponse>;

interface UseEmailNotificationAttributionOptions {
  authenticatedUserId?: string;
  pathname: string;
  searchParams: string;
  surface: Extract<EmailNotificationSurface, 'fan_web' | 'creator_web'>;
  verifyLanding: VerifyEmailNotificationLanding;
}

const DEFINITIVE_VERIFIER_STATUSES = new Set([400, 401, 404, 410, 422]);
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Retains signed email attribution through login, scrubs history, and verifies ownership once. */
export function useEmailNotificationAttribution({
  authenticatedUserId,
  pathname,
  searchParams,
  surface,
  verifyLanding,
}: UseEmailNotificationAttributionOptions): void {
  const serializedSearchParams = searchParams;
  const inFlightKeys = useRef(new Set<string>());
  const [verificationRetryRevision, setVerificationRetryRevision] = useState(0);

  useBrowserLayoutEffect(() => {
    if (!serializedSearchParams) return;
    const parameters = new URLSearchParams(serializedSearchParams);
    const destinationReference = readEmailAttributionDestinationReference(parameters, pathname);
    const attribution = parseEmailNotificationAttribution(
      parameters,
      Date.now(),
      pathname,
      destinationReference,
    );
    if (attribution) {
      storePendingEmailAttribution(sessionStorage, attribution);
      sessionStorage.setItem('notification_email_disable_session_replay', 'true');
    }
    const sanitizedParameters = attribution
      ? stripEmailAttributionQuery(parameters)
      : stripSensitiveEmailAttributionQuery(parameters);
    if (sanitizedParameters.toString() === parameters.toString()) return;
    const remainingQuery = sanitizedParameters.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${pathname}${remainingQuery ? `?${remainingQuery}` : ''}${window.location.hash}`,
    );
  }, [pathname, serializedSearchParams]);

  useEffect(() => {
    const retryAfterReconnect = () => setVerificationRetryRevision((revision) => revision + 1);
    window.addEventListener('online', retryAfterReconnect);
    return () => window.removeEventListener('online', retryAfterReconnect);
  }, []);

  useBrowserLayoutEffect(() => {
    if (!authenticatedUserId) return;
    const pendingAttributions = readPendingEmailAttributions(sessionStorage);

    pendingAttributions.forEach((pending) => {
      const requestKey = `${pending.deliveryId}:${pending.linkKey}`;
      if (inFlightKeys.current.has(requestKey)) return;
      inFlightKeys.current.add(requestKey);
      markWebEmailAttributionVerificationStarted(pending);

      void verifyLanding(buildEmailNotificationLandingRequest(pending, surface))
        .then((response) => {
          const verified = buildVerifiedEmailAttribution(pending, response, authenticatedUserId);
          if (verified) {
            storeVerifiedEmailAttribution(sessionStorage, verified);
          }
          removePendingEmailAttribution(sessionStorage, pending);
          if (readPendingEmailAttributions(sessionStorage).length === 0) {
            sessionStorage.removeItem(EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY);
          }
        })
        .catch((error) => {
          const status = Number(error?.status || error?.originalStatus);
          if (status === 403) {
            sessionStorage.setItem(EMAIL_ATTRIBUTION_ACCOUNT_SWITCH_REQUIRED_STORAGE_KEY, 'true');
          } else if (DEFINITIVE_VERIFIER_STATUSES.has(status)) {
            removePendingEmailAttribution(sessionStorage, pending);
          }
        })
        .finally(() => {
          markWebEmailAttributionVerificationCompleted(pending);
          window.dispatchEvent(
            new CustomEvent(EMAIL_ATTRIBUTION_CONTEXT_CHANGED_EVENT, {
              detail: {
                destinationPath: pending.destinationPath,
                destinationReference: pending.destinationReference,
              },
            }),
          );
          inFlightKeys.current.delete(requestKey);
        });
    });
  }, [authenticatedUserId, surface, verificationRetryRevision, verifyLanding]);
}
